import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import {
    Brush,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getLineColor } from "../../../utils/getLineColor";

export const ShareOfVoiceOverTime = (props) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [getVolume] = useGetConceptsFiltersMutation();
    const [maxPercentage, setMaxPercentage] = useState(0);
    const [conceptColors, setConceptColors] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });

    const {
        selectedDateName,
        interval,
        id,
        filters,
        conceptList,
        visualizeType,
        setFilterDocCount,
        setLocalConceptOptions,
        seletctedType,
        smoothLine
    } = props;
    const additonalInfo = useMemo(() => ({}), []);

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props]
    );

    const getVolumeData = useCallback(async () => {
        setLoading(true);

        const { data: dataConcepts } = await getVolume({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            breakdowns: [{ name: selectedDateName, interval }],
        });

        setFilterDocCount(dataConcepts.filter_count);

        if (conceptList) {
            setLocalConceptOptions(dataConcepts.match_counts);
        }

        const breakdownData = dataConcepts.breakdowns[0].buckets;
        const conceptsMap = {};
        const conceptColors = {};

        breakdownData.forEach(
            ({ label, match_counts, total_count, filter_count }) => {
                if (!filter_count) return;
                const formattedDate = formatDateToUtc(label, interval);
                match_counts.forEach(
                    ({ name, match_count, exact_match_count,color }) => {
                        if (!conceptsMap[name]) {
                            conceptsMap[name] = [];
                            if (color) conceptColors[name] = color;
                        }

                        let count = match_count;
                        switch (seletctedType) {
                            case "exact_match_count":
                                count = exact_match_count;
                                break;
                            case "conceptual_match_count":
                                count = match_count - exact_match_count;
                                break;
                            default:
                                count = match_count;
                                break;
                        }

                        let value = {
                            [`${name} %`]: (
                                (count / dataConcepts.total_count) *
                                100
                            ).toFixed(1),
                        };

                        if (visualizeType) {
                            value = {
                                [name]: total_count
                                    ? ((count / total_count) * 100).toFixed(1)
                                    : "0",
                            };

                            additonalInfo[`${name}-${formattedDate}`] = {
                                count,
                                percent:
                                    total_count === 0
                                        ? 0
                                        : ((count / total_count) * 100).toFixed(
                                            1
                                        ),
                            };
                        }

                        conceptsMap[name].push({
                            date: formattedDate,
                            ...value,
                        });
                    }
                );
            }
        );

        const aggregatedData = Array.from(
            Object.values(conceptsMap)
                .flat()
                .reduce((acc, curr) => {
                    if (acc.has(curr.date)) {
                        acc.set(curr.date, { ...acc.get(curr.date), ...curr });
                    } else {
                        acc.set(curr.date, curr);
                    }
                    return acc;
                }, new Map())
                .values()
        );

        setChartData(aggregatedData);

        const maxVal = Math.max(
            ...aggregatedData.map((entry) =>
                Math.max(
                    ...Object.keys(entry)
                        .filter((key) => key !== "date")
                        .map((key) => parseFloat(entry[key]))
                )
            )
        );
        setMaxPercentage(Math.min(maxVal , 100));
        setConceptColors(conceptColors);
        setLoading(false);
    }, [
        additonalInfo,
        conceptFilter,
        conceptList,
        filters,
        getVolume,
        id,
        interval,
        selectedDateName,
        seletctedType,
        setFilterDocCount,
        setLocalConceptOptions,
        visualizeType,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetVolume = debounce(getVolumeData, 1000);

        debouncedGetVolume();

        return () => {
            debouncedGetVolume.cancel();
        };
    }, [getVolumeData]);

    if (loading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    let labelText;
    switch (props.seletctedType) {
        case "exact_match_count":
            labelText = "Exact Matches %";
            break;
        case "conceptual_match_count":
            labelText = "Conceptual Matches %";
            break;
        default:
            labelText = "Total Matches %";
            break;
    }
    if (visualizeType) {
        labelText = "% of each subset";
    }
    const renderTooltipContent = (o) => {
        const { payload, label } = o;
        const getLabelText = ({ name, value }) => {
            if (visualizeType) {
                const { count, percent } =
                additonalInfo[`${name}-${label}`] || {};
                return `${name}: ${count} (${percent}%)`;
            }

            return `${name} : ${value}%`;
        };

        return (
            <div className="custom-tooltip">
                <p className="label">{`${label}`}</p>
                {payload?.map((entry, index) => (
                    <p
                        key={`item-${index}`}
                        style={{ color: entry.color, fontSize: labelFontSize }}
                    >
                        {getLabelText(entry)}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <div className="volume-compare"  style={{ height: props.widget.layout.h * 39 - 150 }}  onMouseDown={(e) => {
            if (e.target.closest(".recharts-brush")) {
                e.stopPropagation();
                e.preventDefault()
            }}}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}/>
                    <YAxis
                        label={{
                            value: `${labelText} `,
                            angle: -90,
                            position: "insideLeft",
                            offset: visualizeType ? 0 : 0,
                            style: {
                                textAnchor: "middle",
                                fontSize: 16,
                                fontWeight: "bold",
                                color: "#000"
                            },
                        }}
                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                        domain={[0, maxPercentage]}
                    />
                    <Tooltip content={renderTooltipContent}  wrapperStyle={{ fontSize: 16, color: "#000", fontWeight: "bold" }}/>
                    <Legend
                        wrapperStyle={{
                            fontSize: `${labelFontSize}px`,
                            fontWeight: "bold",
                            color: "#000",
                        }}
                    />
                    <Brush
                        dataKey="date"
                        height={30}
                        stroke="#696969"
                        travellerWidth={12}
                        gap={2}
                        startIndex={0}
                        endIndex={chartData.length - 1}
                        fill="#D3D3D3"
                        travellerStyle={{
                            stroke: "#696969",
                            fill: "#A9A9A9",
                            cursor: "pointer",
                        }}
                        selectedBoxStyle={{
                            stroke: "#A9A9A9",
                            fill: "rgba(169, 169, 169, 0.2)",
                            cursor: "move",
                        }}
                        padding={{ top: 2, bottom: 2 }}
                    />
                    {Object.keys(chartData[0] ?? {})
                        .filter((key) => key !== "date")
                        .map((key, idx) => (
                            <Line
                                type={smoothLine ? "monotone" : "linear"}
                                dot={!smoothLine}
                                dataKey={`${key}`}
                                stroke={conceptColors[key] || getLineColor(idx)}
                                strokeWidth={idx % 2 === 0 ? 2 : 1}
                                key={key}
                            />
                        ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
